import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Linha de lista (fora de tabela): nome em Lexend, contexto abaixo, status e uma ação à direita.
 * É a forma que a tabela assume no celular, a lista de pendências do dashboard, a lista de documentos
 * na ficha e a lista de PDFs gerados. Linha crítica ganha fio plum de 2px à esquerda.
 */
export interface ListRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode
  /** Contexto abaixo do título ("Bigode chinês · R$ 3.000 · 33 dias em aberto"). */
  meta?: ReactNode
  /** Etiqueta de status, ação em texto ou botão. */
  trailing?: ReactNode
  /** Rótulo em caixa alta acima do título ("14:00"). */
  eyebrow?: ReactNode
  critical?: boolean
  /** Tamanho do título: `name` (15px, cartão no celular) ou `lead` (14px, lista densa). */
  size?: 'name' | 'lead'
}

export function ListRow({
  title,
  meta,
  trailing,
  eyebrow,
  critical,
  size = 'lead',
  className,
  onClick,
  ...props
}: ListRowProps) {
  const interactive = Boolean(onClick)
  return (
    <div
      data-slot="list-row"
      data-critical={critical || undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>)
              }
            }
          : undefined
      }
      className={cn(
        'group min-h-touch border-hairline flex items-center justify-between gap-4 border-b px-4 py-[14px] last:border-b-0',
        interactive && 'hover:bg-pampas focus-visible:bg-pampas cursor-pointer',
        critical && 'shadow-[inset_2px_0_0_var(--color-plum)]',
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1">
        {eyebrow ? <div className="eyebrow mb-1">{eyebrow}</div> : null}
        <div
          className={cn(
            'font-display text-ink font-normal',
            size === 'name' ? 'text-name' : 'text-lead leading-[1.35]',
          )}
        >
          {title}
        </div>
        {meta ? (
          <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
            {meta}
          </div>
        ) : null}
      </div>
      {trailing ? (
        <div className="flex shrink-0 items-center gap-3" onClick={(e) => e.stopPropagation()}>
          {trailing}
        </div>
      ) : null}
    </div>
  )
}

/** Contêiner das linhas: superfície branca com fio. */
export function ListRows({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="list-rows"
      className={cn('border-hairline bg-surface border', className)}
      {...props}
    />
  )
}
